import Link from "next/link";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "./cx";
import styles from "./page-header.module.css";

export interface PageHeaderProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "title"
> {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  backHref?: string;
  backLabel?: string;
  noBorder?: boolean;
  eyebrow?: string;
  accentWord?: string;
  /** Render the subtitle inline on the title's line (muted) instead of below. */
  inlineSubtitle?: boolean;
}

function renderTitle(title: ReactNode, accentWord?: string): ReactNode {
  if (!accentWord || typeof title !== "string") return title;
  const trimmed = title.trimEnd();
  const target = accentWord.trim();
  if (!target) return title;
  if (trimmed.toLowerCase().endsWith(target.toLowerCase())) {
    const prefix = trimmed.slice(0, trimmed.length - target.length);
    const matched = trimmed.slice(trimmed.length - target.length);
    return (
      <>
        {prefix}
        <span className={styles.accentWord}>{matched}</span>
      </>
    );
  }
  return title;
}

export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(
  (
    {
      className,
      title,
      subtitle,
      actions,
      backHref,
      backLabel = "Back",
      noBorder,
      eyebrow,
      accentWord,
      inlineSubtitle,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={cx(
        styles.header,
        noBorder && styles.headerNoBorder,
        className,
      )}
      {...props}
    >
      <div className={styles.left}>
        {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        {backHref ? (
          <Link href={backHref} className={styles.back}>
            ← {backLabel}
          </Link>
        ) : null}
        <h1 className={styles.title}>
          {renderTitle(title, accentWord)}
          {inlineSubtitle && subtitle ? (
            <span className={styles.subtitleInline}>{subtitle}</span>
          ) : null}
        </h1>
        {subtitle && !inlineSubtitle ? (
          <p className={styles.subtitle}>{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </div>
  ),
);

PageHeader.displayName = "PageHeader";
